import type { ReactNode } from 'react'
import type { EditorMode } from '@/msgdeskRuntime/contracts/editorActions'
import { legacyHookAttrs, type LegacyContractName } from '@/msgdeskRuntime/legacyBridge/SelectorContracts'
import { styleFromString } from '@/msgdeskRuntime/legacyBridge/legacyActions'
import { useSubsStore } from '@/state/MsgTreeProvider'
import { AnswersPanel, MenuSubsPanels, MessagesPanel } from '@/deskUiSkeletons/Page/Panels'
import { EnterPinForm, LoadingOverlay, QuickReply, ShellOverlays } from '@/deskUiSkeletons/Shared/Overlays'
import { PinDialog } from '@/deskUiSkeletons/Shared/PinDialog'
import { PooMainDocument } from './PooMainDocument'
import { PooMenu } from './PooMenu'
import { PooProtocolPanel } from './PooProtocolPanel'
import { PooInstructions } from './PooInstructions'
import { PooSetupPanel } from './PooSetupPanel'

type WorkspaceKind = 'main' | 'editor' | 'attachments' | 'favs' | 'trash' | 'drafts'

const workspaceByKind: Record<WorkspaceKind, { contract: LegacyContractName; style: string }> = {
  main: { contract: 'workspace.main', style: '' },
  editor: { contract: 'workspace.editor', style: 'height:100%;' },
  attachments: { contract: 'workspace.attachments', style: '' },
  favs: { contract: 'workspace.favs', style: '' },
  trash: { contract: 'workspace.trash', style: '' },
  drafts: { contract: 'workspace.drafts', style: '' },
}

function WorkspaceSection({ kind, children }: { kind: WorkspaceKind; children?: ReactNode }) {
  const { state } = useSubsStore()
  const info = workspaceByKind[kind]
  const visible = state.chromeByContract[info.contract]?.visible !== false
  return (
    <section
      {...legacyHookAttrs(info.contract, `md-poo-workspace md-poo-workspace--${kind} min-w-0${visible ? '' : ' hidden'}${kind === 'main' || kind === 'editor' ? ' flex-1' : ''}`)}
      style={{ ...styleFromString(info.style), ...(visible ? {} : { display: 'none' }) }}
      {...(visible ? {} : { 'data-md-react-display': 'none' })}
    >
      {children}
    </section>
  )
}

function RouteHeading({ title, detail }: { title: string; detail: string }) {
  return (
    <header className="mb-4 rounded-xl border border-[#d7d1c5] bg-white px-5 py-4">
      <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#68746f]">Opening Proof Protocol workspace</div>
      <h1 className="mt-1 text-xl font-semibold text-[#1d2a26]">{title}</h1>
      <p className="mt-1 text-sm text-[#68716d]">{detail}</p>
    </header>
  )
}

export function PooPage({ isEditorOpen = false, editorMode }: { isEditorOpen?: boolean; editorMode?: EditorMode }) {
  const { state } = useSubsStore()
  const mode = state.renderProjection.mode
  const editorActive = isEditorOpen || mode === 'editor'
  const isSetup = state.pooProtocol?.lifecycleState === 'setup'

  return (
    <>
      <ShellOverlays />
      <div {...legacyHookAttrs('page.root', 'md-poo-page min-h-screen w-full bg-[#f3f1ec] text-[#25302d]')}>
        <div {...legacyHookAttrs('page.table', 'md-poo-page-table min-h-screen w-full')}>
          <div {...legacyHookAttrs('page.header', 'h-16')}>
            <div {...legacyHookAttrs('page.headerCell', 'h-16')} />
          </div>
          <div className="mx-auto flex w-full max-w-[1180px]">
            <div {...legacyHookAttrs('page.accentCell', 'hidden')} hidden aria-hidden="true" />
            <main {...legacyHookAttrs('page.workspaceCell', 'min-w-0 flex-1 px-3 pb-12 sm:px-5 lg:px-7')}>
              <WorkspaceSection kind="main">
                {isSetup ? <PooSetupPanel /> : mode === 'msg' ? (
                  <>
                    <PooMainDocument />
                    <PooInstructions />
                    <PooProtocolPanel />
                    <RouteHeading title="Answers" detail="User-authored answers follow the immutable protocol subs recorded above." />
                    <AnswersPanel />
                  </>
                ) : null}
                {!isSetup && mode === 'answers' ? (
                  <>
                    <RouteHeading title="Answers" detail="Answers use the established standard comment-row composition in OPP-owned visual code." />
                    <AnswersPanel />
                  </>
                ) : null}
                {!isSetup && mode === 'submsges' ? (
                  <>
                    <RouteHeading title="Messages" detail="Messages stay off the document root and are available through this separate menu route." />
                    <MessagesPanel />
                  </>
                ) : null}
              </WorkspaceSection>
              <WorkspaceSection kind="editor">
                <RouteHeading title={editorMode === 'update_msg' ? 'Protected document' : 'New response'} detail={editorMode === 'update_msg' ? 'An OPP document is immutable after creation.' : 'Responses use the normal MsgDesk publishing flow and are not part of the sealed proof content.'} />
              </WorkspaceSection>
              <WorkspaceSection kind="attachments">
                <RouteHeading title="Files" detail="Protected attachment projection will be owned by the OPP serializer and access gate." />
              </WorkspaceSection>
              <WorkspaceSection kind="favs"><RouteHeading title="Favorites" detail="Saved subs for this document." /><MenuSubsPanels mode="favs" /></WorkspaceSection>
              <WorkspaceSection kind="trash"><RouteHeading title="Trash" detail="Removed subs for this document." /><MenuSubsPanels mode="trash" /></WorkspaceSection>
              <WorkspaceSection kind="drafts"><RouteHeading title="Drafts" detail="Unpublished subs for this document." /><MenuSubsPanels mode="drafts" /></WorkspaceSection>
            </main>
            <div {...legacyHookAttrs('page.rightRail', 'hidden')} hidden aria-hidden="true" />
          </div>
          <footer {...legacyHookAttrs('page.footer', 'mx-auto max-w-[1180px] px-7 pb-5 text-[10px] uppercase tracking-[0.16em] text-[#7a817e]')}>MsgDesk Opening Proof Protocol</footer>
        </div>
      </div>
      <PooMenu isEditorOpen={editorActive} editorMode={editorMode} />
      <QuickReply />
      <EnterPinForm />
      <LoadingOverlay />
      <PinDialog />
    </>
  )
}
